import qs from 'qs';
import { ResizeThrottler } from './utils';

const resizeThrottler = new ResizeThrottler();

// Height of filter lists, along with the top margin
// If you edit this, change it in the css as well
const FILTER_HEIGHT = 285 + 35;

class EntryFilter {
  constructor(el) {
    this.el = el;
    this.label = el.querySelector('.filter-item__label');
    this.list = el.querySelector('.filter-item__list');
    this.items = [...this.el.querySelectorAll('.filter-item__item label')];
    this.queryParam = el.getAttribute('data-query-param');
    this.isMultiSelect = el.getAttribute('data-multi-select');
    this.clearOnSelect = el.getAttribute('data-clear-on-select');
    this.filterContainer = document.querySelector('.entry-filter__filters');

    // Attach "Select all" and "Clear all" handlers
    if (this.isMultiSelect) {
      this.el.querySelector('.filter-item__select-all')
        .addEventListener('click', this.selectAll.bind(this));
      this.el.querySelector('.filter-item__clear-all')
        .addEventListener('click', this.deselectAll.bind(this));
    }

    // Open and close the dropdowns
    this.label.addEventListener('click', () => {
      this.toggleDropdown();
    });

    this.items.forEach((item) => {
      const input = item.querySelector('input');
      // Emulate :focus styles
      input.addEventListener('focus', () => {
        item.classList.add('focused');
      });
      input.addEventListener('blur', () => {
        item.classList.remove('focused');
      });
      // Handle selecting and deselecting
      input.addEventListener('change', this.handleItemChange.bind(this));
    });
  }

  addSiblingFilters(filters) {
    this.siblingFilters = filters;
  }

  toggleDropdown() {
    if (this.list.style.display === 'block') this.closeDropdown();
    else this.openDropdown();
  }

  openDropdown() {
    // First, close other dropdowns
    if (this.siblingFilters) {
      this.siblingFilters.forEach(siblingFilter => siblingFilter.closeDropdown(true));
    }
    // Then open this one
    this.el.classList.add('open');
    this.list.style.display = 'block';
    this.clampFilter();
    this.el.style.height = this.el.offsetHeight + Math.min(this.list.offsetHeight + 35, FILTER_HEIGHT) + 'px';
    this.showShadow();
  }

  clampFilter() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const listLeftDistance = this.list.getBoundingClientRect().left;
    const filterContainerRightDistance = windowWidth - this.filterContainer.getBoundingClientRect().right;

    this.list.style.width = 'calc(100vw - ' + (listLeftDistance + 20) + 'px)';
    this.list.style.maxWidth = 'calc(100vw - ' + (listLeftDistance + filterContainerRightDistance) + 'px)';
  }

  closeDropdown(keepShadow) {
    this.el.classList.remove('open');
    this.list.style.display = 'none';
    this.el.style.height = 'auto';
    if (!keepShadow) this.hideShadow();
  }

  hideShadow() {
    document.querySelector('.entry-filter').classList.remove('expanded');
  }

  showShadow() {
    document.querySelector('.entry-filter').classList.add('expanded');
  }

  handleItemChange(e) {
    if (!this.isMultiSelect) {
      this.selectRadioItem(e.currentTarget);
    } else if (e.currentTarget.checked) {
      this.selectCheckboxItem(e.currentTarget);
    } else {
      this.deselectCheckboxItem(e.currentTarget);
    }
  }

  selectRadioItem(item) {
    // Create new URL
    const oldQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const newParams = {
      ...oldQuery,
      [this.queryParam]: item.getAttribute('value'),
    };
    this.navigate(newParams);
    // Deselect other labels
    this.items.forEach((label) => {
      label.classList.remove('selected');
    });
    // Update this label's style
    item.parentElement.classList.add('selected');
  }

  deselectCheckboxItem(item) {
    // Create new URL
    const oldQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const newParams = {
      ...oldQuery,
      [this.queryParam]: oldQuery[this.queryParam].filter(param => param !== item.getAttribute('value')),
    };
    this.navigate(newParams);
    // Update label style
    item.parentElement.classList.remove('selected');
  }

  selectCheckboxItem(item) {
    // Create new URL
    const oldQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const newParams = {
      ...oldQuery,
      [this.queryParam]: [
        ...(oldQuery[this.queryParam] || []),
        item.getAttribute('value'),
      ],
    };
    this.navigate(newParams);
    // Update label style
    item.parentElement.classList.add('selected');
  }

  navigate(newParams) {
    const baseUrl = window.location.origin + window.location.pathname;
    if (this.clearOnSelect) {
      newParams[this.clearOnSelect] = undefined; // remove this key from the output
    }
    const newQuery = qs.stringify(newParams, {
      arrayFormat: 'brackets',
      addQueryPrefix: true,
    });
    // if (window.history && window.history.pushState) {
    if (false) {
      window.history.pushState(null, null, baseUrl + newQuery);
    } else {
      window.location = baseUrl + newQuery;
    }
  }

  deselectAll() {
    // Create new URL
    const oldQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const newParams = {
      ...oldQuery,
      [this.queryParam]: undefined,
    };
    this.navigate(newParams);
    // Update label style
    this.items.forEach((item) => {
      item.parentElement.classList.remove('selected');
    });
  }

  selectAll() {
    // Create new URL
    const oldQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const newParams = {
      ...oldQuery,
      [this.queryParam]: this.items.map(item => item.querySelector('input').getAttribute('value')),
    };
    this.navigate(newParams);
    // Update label style
    this.items.forEach((item) => {
      item.parentElement.classList.add('selected');
    });
  }
}

export default function initEntryFilters() {
  // Set up filters
  const entryFilters = [...document.querySelectorAll('.filter-item')].map(el => new EntryFilter(el));
  entryFilters.forEach((entryFilter) => {
    entryFilter.addSiblingFilters(entryFilters.filter(item => item !== entryFilter));
    resizeThrottler.add(() => entryFilter.clampFilter());
  });

  // Set up search listeners
  const search = document.querySelector('.entry-filter__search');
  if (search) {
    let searchExpanded;
    const button = search.querySelector('.entry-filter__search-button');
    button.addEventListener('click', (e) => {
      if (!searchExpanded) {
        e.preventDefault();
        search.classList.add('entry-filter__search--open');
        searchExpanded = true;
        search.querySelector('input').focus();
        if (window.flexibility) window.flexibility(document.documentElement);
      }
    });
    // Close it back up when someone clicks away
    document.addEventListener('click', (e) => {
      if (!search.contains(e.target)) {
        search.classList.remove('entry-filter__search--open');
        searchExpanded = false;
        if (window.flexibility) window.flexibility(document.documentElement);
      }
    });
  }

  // Close all filters when someone clicks elsewhere
  const filterContainer = document.querySelector('.entry-filter__filters');
  if (filterContainer) {
    document.addEventListener('click', (e) => {
      if (!filterContainer.contains(e.target)) {
        entryFilters.forEach(entryFilter => entryFilter.closeDropdown());
      }
    });
  }
}
